import React, { useEffect } from 'react'
import { withRouter, RouteComponentProps } from 'react-router'
import { useDispatch, useSelector } from 'react-redux'
import { Spin } from 'antd'

import { ApplicationState } from 'src/store'
import {
  getReports,
  getReportsResource,
  resetResources
} from 'src/store/ducks/reports/actions'

import {
  R1,
  R2,
  R3,
  R4,
  R5,
  R6,
  R7,
  R8,
  R9,
  R10,
  R11,
  R12,
  R13,
  R14,
  R15,
  ReportNotFound
} from './components'
import { ReportParameter } from '../../types'
import { WarpCenter } from './styles'
import { WrapAlert } from '../../styles'

function parseReportFilters(rawFilters: string): ReportParameter[] {
  try {
    const filterParsed = JSON.parse(rawFilters).filter
    const filters = Object.entries(filterParsed)
      .filter(item => 'tenant_id' !== item?.[0])
      .map((item: any) => {
        const required = item[1].indexOf('?') < 0
        return {
          name: item[0],
          type: required ? item[1] : item[1].substring(1),
          required
        }
      })
    return filters
  } catch (error) {
    return []
  }
}

function matchFiltersParams(
  filters: ReportParameter[],
  params: URLSearchParams
) {
  return filters.map(({ name, required }: ReportParameter) => {
    const paramValue = params.get(`${name}`)
    if (required && ['', 'null', 'NULL'].includes(paramValue)) {
      throw new Error('web.report.parse-error')
    }
    return `${[encodeURI(name)]}=${encodeURI(paramValue || 'NULL')}`
  })
}

function parseReportResources(rawResources: any): {} {
  try {
    return JSON.parse(rawResources || '')
  } catch (error) {
    return {}
  }
}

function ReportRender({ report }: { report: string }) {
  switch (report) {
    case 'R1':
      return <R1 />
    case 'R2':
      return <R2 />
    case 'R3':
      return <R3 />
    case 'R4':
      return <R4 />
    case 'R5':
      return <R5 />
    case 'R6':
      return <R6 />
    case 'R7':
      return <R7 />
    case 'R8':
      return <R8 />
    case 'R9':
      return <R9 />
    case 'R10':
      return <R10 />
    case 'R11':
      return <R11 />
    case 'R12':
      return <R12 />
    case 'R13':
      return <R13 />
    case 'R14':
      return <R14 />
    case 'R15':
      return <R15 />
    default:
      return <ReportNotFound />
  }
}

/**
 * @todo
 * Isolate each report with its own get,
 * loading and error display format
 */

function Report({
  match: {
    params: { report_id: report_id_string }
  }
}: RouteComponentProps<{ report_id: string }>) {
  const {
    reports: { error, loading, reportsList, reportsResources }
  } = useSelector((state: ApplicationState) => ({ reports: state.reports }))

  const dispatch = useDispatch()
  const report_id = report_id_string.trim()

  useEffect(() => {
    let isSubscribed = true
    const hasTheReport = reportsList.find(
      item => item.report_data_id === Number(report_id)
    )

    if (!loading) {
      if (!hasTheReport) {
        if (isSubscribed) {
          dispatch(resetResources())
          dispatch(getReports(report_id))
        }
      } else if (!error) {
        const reportFilters = parseReportFilters(hasTheReport.filters)
        const urlParams = new URLSearchParams(decodeURI(window.location.search))
        let matchFilters: string[] = []

        try {
          matchFilters = matchFiltersParams(reportFilters, urlParams)
        } catch (e) {}

        const queryString = matchFilters.length
          ? `?${matchFilters.join('&')}`
          : ''

        const resources = parseReportResources(hasTheReport.resources)
        Object.entries(resources).forEach(([key, value]: any) => {
          if (isSubscribed) {
            dispatch(getReportsResource(key, `${value}${queryString}`))
          }
        })
      }
    }

    return () => {
      isSubscribed = false
    }
  }, [dispatch, error, loading, report_id, reportsList])

  const isLoading =
    loading || Object.values(reportsResources).some(item => item.loading)
  const hasError =
    error || Object.values(reportsResources).some(item => item.error)

  if (hasError) {
    return <WrapAlert message="web.report.load-resource-error" />
  }

  if (isLoading) {
    return (
      <WarpCenter>
        <Spin size="default" />
      </WarpCenter>
    )
  }

  const report = reportsList.find(
    item => item.report_data_id === Number(report_id)
  )

  return <ReportRender report={report?.report_class} />
}

export default withRouter(Report)
